import { formatValue } from './format-value';
import type { ChartLabelFormat, NormalizedBarChartData } from './types';

const UTF8_BOM = '\uFEFF';
const ROW_SEPARATOR = '\r\n';
const DEFAULT_CATEGORY_HEADER = 'Category';

const FORMULA_TRIGGERS = new Set(['=', '+', '-', '@', '\t', '\r']);

/** RFC 4180: wrap in quotes and double any embedded quotes when a field needs it. */
const rfcQuote = (field: string): string =>
  /["\r\n,]/.test(field) ? `"${field.replaceAll('"', '""')}"` : field;

/** Escape labels/categories with injection hardening + RFC quoting. */
const escapeText = (raw: string): string => {
  const guarded =
    raw.length > 0 && FORMULA_TRIGGERS.has(raw[0]) ? `'${raw}` : raw;

  return rfcQuote(guarded);
};

/**
 * Numeric cells are formatted to match in chat table formatting.
 * They are not escaped as raw input values should only ever be numbers (no risk for malicious characters)
 */
const formatNumber = (
  value: number | null | undefined,
  format: ChartLabelFormat
): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '';
  }

  return rfcQuote(formatValue(value, format));
};

/** Serializes normalized bar-chart data into a UTF-8 (BOM-prefixed) CSV string. */
export const barChartToCsv = (data: NormalizedBarChartData): string => {
  const { categoryAxis, series, format } = data;

  const headerRow = [
    escapeText(categoryAxis.label || DEFAULT_CATEGORY_HEADER),
    ...series.map((singleSeries) => escapeText(singleSeries.label)),
  ];

  const bodyRows = categoryAxis.categories.map((category, categoryIndex) => [
    escapeText(category),
    ...series.map((singleSeries) =>
      formatNumber(singleSeries.values[categoryIndex], format)
    ),
  ]);

  const csvBody = [headerRow, ...bodyRows]
    .map((row) => row.join(','))
    .join(ROW_SEPARATOR);

  return `${UTF8_BOM}${csvBody}`;
};
